import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import ResponsiveButton from "../ResponsiveButton/ResponsiveButton";
import {
  MdAddShoppingCart,
  MdRemoveShoppingCart,
  MdInfo,
  MdEventAvailable,
} from "react-icons/md";
import { useState, useEffect } from "react";
import { trackEvent } from "../../utils/analytics";

const formatPrice = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n % 1 === 0 ? String(n) : n.toFixed(2).replace(".", ",");
};

const ProductCard = ({
  item,
  addToCart,
  removeFromCart,
  cartItems,
  isBooked,
  isDisabled,
  hideCartButtons,
  hideQuantitySelector,
  hideAvailableCount,
}) => {
  const [isInCart, setIsInCart] = useState(false);
  const [localQuantity, setLocalQuantity] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const checkCart = () => {
      const cart = JSON.parse(localStorage.getItem("rentalCart")) || [];
      setIsInCart(cart.some((c) => c.id === item.id));
    };

    checkCart();
    window.addEventListener("cartUpdated", checkCart);
    return () => window.removeEventListener("cartUpdated", checkCart);
  }, [item.id]);

  const quantityInCart = cartItems.reduce(
    (sum, c) => (c.id === item.id ? sum + c.quantity : sum),
    0
  );

  const stock = parseInt(item.available, 10) || 0;
  const availableTotal = stock - quantityInCart;
  const isTrailer = item.id === 24;
  const finalDisable = (isDisabled ?? false) || (isBooked && !isTrailer);

  const productUrl = item.link || `/tuote/${item.slug}`;
  const imageUrl = item.image || item.images?.[0]?.image_url || "/assets/placeholder.jpg";
  const priceText = formatPrice(item.price);

  return (
    <article
      className={`
        group relative flex h-full flex-col overflow-hidden rounded-3xl
        border border-[#DDE7E5] bg-white p-5 shadow-sm
        transition-all duration-200 ease-out
        hover:-translate-y-1 hover:border-primary hover:shadow-xl
        dark:border-darkborder dark:bg-darksurface
        dark:hover:border-primary dark:hover:bg-darksurface2
        ${finalDisable ? "opacity-50 pointer-events-none" : ""}
      `}
    >
      {finalDisable && !isTrailer && (
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <div
            className="
              rotate-[-12deg] rounded-xl bg-red-600 px-6 py-2
              text-2xl font-extrabold tracking-widest text-white
              shadow-2xl ring-2 ring-red-300
              md:text-3xl
            "
          >
            VARATTU
          </div>
        </div>
      )}

      {/* Kuva */}
      <Link
        to={productUrl}
        title={item.name}
        aria-label={`Avaa tuote ${item.name}`}
        onClick={() => {
          trackEvent("tuote_avattu", {
            tuote_id: item.id,
            tuote_nimi: item.name,
            sijainti: "productcard",
          });
          window.scrollTo({ top: 0, behavior: "auto" });
        }}
        className="
          relative mb-4 block overflow-hidden rounded-2xl
          border border-[#E5E7EB] bg-[#F8FAFC]
          dark:border-darkborder dark:bg-[#111114]
        "
      >
        <div
          aria-hidden="true"
          className="
            absolute inset-x-8 bottom-6 h-14 rounded-full bg-primary/15 blur-2xl
            transition group-hover:bg-primary/25
          "
        />

        <img
          src={imageUrl}
          alt={item.name || "Tuotekuva"}
          loading="lazy"
          className="
            relative z-10 h-[150px] w-full object-contain p-4
            drop-shadow-[0_14px_16px_rgba(0,0,0,0.18)]
            transition duration-200 group-hover:scale-[1.03]
          "
        />
      </Link>

      {/* Sisältö */}
      <div className="flex flex-1 flex-col">
        <div className="mb-3 flex flex-wrap items-center justify-center gap-2">
          {item.kategoriaID === 1 && (
            <span
              className="
                rounded-full border border-primary/40 bg-primary/10 px-3 py-1
                font-customFont1 text-xs font-bold !text-[#111827]
                dark:!text-white
              "
            >
              Äänentoisto
            </span>
          )}

          {item.kategoriaID === 2 && (
            <span
              className="
                rounded-full border border-primary/40 bg-primary/10 px-3 py-1
                font-customFont1 text-xs font-bold !text-[#111827]
                dark:!text-white
              "
            >
              Valotekniikka
            </span>
          )}

          {item.kategoriaID === 3 && (
            <span
              className="
                rounded-full border border-primary/40 bg-primary/10 px-3 py-1
                font-customFont1 text-xs font-bold !text-[#111827]
                dark:!text-white
              "
            >
              Muu kalusto
            </span>
          )}
        </div>

        {/* Nimi */}
        <h3
          className="
            text-center font-customFont1 text-base font-extrabold uppercase
            leading-snug !text-[#111827]
            dark:!text-white
          "
        >
          {item.name}
        </h3>

        {/* Saatavuus */}
        {!hideAvailableCount && stock > 1 && (
          <p
            className="
              mt-2 text-center font-customFont1 text-sm font-semibold
              !text-[#4B5563]
              dark:!text-[#D1D5DB]
            "
          >
            Vuokrattavissa: {availableTotal}
          </p>
        )}

        {/* Hinta */}
        {priceText && (
          <p
            className="
              mt-3 text-center font-customFont1 text-xl font-extrabold
              !text-[#111827]
              dark:!text-white
            "
          >
            {priceText} € / 24h
          </p>
        )}

        {/* Määrä */}
        {!hideQuantitySelector && availableTotal > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setLocalQuantity((q) => Math.max(1, q - 1))}
              disabled={localQuantity <= 1}
              className="
                flex h-8 w-8 items-center justify-center rounded-full
                bg-[#111827] font-bold text-white transition
                hover:bg-primary hover:!text-[#111827]
                disabled:cursor-not-allowed disabled:opacity-40
                dark:bg-white dark:!text-[#111827]
              "
              aria-label="Vähennä määrää"
            >
              −
            </button>

            <span
              className="
                min-w-6 text-center font-customFont1 text-sm font-extrabold
                !text-[#111827]
                dark:!text-white
              "
            >
              {localQuantity}
            </span>

            <button
              type="button"
              onClick={() =>
                setLocalQuantity((q) => Math.min(q + 1, availableTotal))
              }
              disabled={localQuantity >= availableTotal}
              className="
                flex h-8 w-8 items-center justify-center rounded-full
                bg-[#111827] font-bold text-white transition
                hover:bg-primary hover:!text-[#111827]
                disabled:cursor-not-allowed disabled:opacity-40
                dark:bg-white dark:!text-[#111827]
              "
              aria-label="Lisää määrää"
            >
              +
            </button>
          </div>
        )}

        {/* Napit */}
        {!hideCartButtons && (
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            {!isTrailer && addToCart && (
              <ResponsiveButton
                variant="productcardaddtocart"
                icon={MdAddShoppingCart}
                disabled={availableTotal <= 0 || isBooked}
                onClick={() => {
                  addToCart(item, localQuantity);
                  trackEvent("tuote_lisatty_koriin", {
                    tuote_id: item.id,
                    tuote_nimi: item.name,
                    maara: localQuantity,
                  });
                  setLocalQuantity(1);
                }}
              >
                Lisää koriin
              </ResponsiveButton>
            )}

            {isInCart && removeFromCart && (
              <ResponsiveButton
                variant="productcardremovefromcart"
                icon={MdRemoveShoppingCart}
                onClick={() => {
                  removeFromCart(item);
                  trackEvent("tuote_poistettu_korista", {
                    tuote_id: item.id,
                    tuote_nimi: item.name,
                  });
                }}
              >
                Poista
              </ResponsiveButton>
            )}

            {isTrailer && (
              <ResponsiveButton
                variant="productcarddetails"
                icon={MdEventAvailable}
                onClick={() => {
                  trackEvent("siirtyi_vuokrauskalenteriin", {
                    tuote_id: item.id,
                    tuote_nimi: item.name,
                  });
                  navigate(`/tuote/${item.slug}#kalenteri`);
                }}
              >
                Vuokrauskalenteri
              </ResponsiveButton>
            )}

            <ResponsiveButton
              variant="productcarddetails"
              icon={MdInfo}
              onClick={() => {
                trackEvent("siirtyi_tuotesivulle", {
                  tuote_id: item.id,
                  tuote_nimi: item.name,
                });
                navigate(`/tuote/${item.slug}`);
              }}
            >
              Lisätiedot
            </ResponsiveButton>
          </div>
        )}
      </div>
    </article>
  );
};

ProductCard.propTypes = {
  item: PropTypes.object.isRequired,
  addToCart: PropTypes.func,
  removeFromCart: PropTypes.func,
  cartItems: PropTypes.array.isRequired,
  isBooked: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hideCartButtons: PropTypes.bool,
  hideQuantitySelector: PropTypes.bool,
  hideAvailableCount: PropTypes.bool,
};

export default ProductCard;